import React, { memo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import styles from "./styles.module.scss";
import Button from "app/components/Button/ButtonVariant";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import closeIcon from "../../../../public/images/close-icon.svg";
import AccountCircleIcon from "../../../../public/images/account-circle-icon.svg";
import DollarIcon from "../../../../public/images/dollar-ico.svg";
import { FormProvider, useForm, UseFormReturn } from "react-hook-form";
import FormItem from "app/components/inputs/FormItem";
import { errorMessageFor } from "app/routes/Calendar/EventForm/helpers";
import { RootState } from "typedefs";
import { useSelector } from "react-redux";
import { StudentProfileModel } from "app/models/scheduling/StudentProfileModel";
import {
  schedulingFetchGet,
  schedulingFetchPost,
} from "app/utils/request/scheduling/fetch";
import useCurrentFacility from "app/utils/hooks/scheduling/useCurrentFacility";
import SchedulingUserSelector from "app/components/SchedulingUserSelector";
import ToolTip from "app/routes/Scheduling/ToolTip";
import ActiveSubscriptionsModal from "../OfferingActionModal/ActiveSubscriptionsModal";
import PaymentMethodDropdown from "app/components/PaymentMethodDropdown";
import { OnlinePaymentTypeOptions } from "app/routes/Calendar/EventForm/paymentHelpers";
import { SelectStyles } from "app/routes/Calendar/EventForm/constants";
import { toast } from "react-toastify";
import { uniqBy } from "lodash";
import { flattenFamilyStudentProfiles } from "app/utils/familyStudentProfiles";

const AssignOfferingModalSubscription = ({
  product,
  isModalOpen,
  modalHandler,
  stripeIntegrationEnabled = true,
  facilityId,
}) => {
  type FormValues = {
    studentProfiles: StudentProfileModel[];
  };

  const methods: UseFormReturn<FormValues> = useForm<FormValues>({
    defaultValues: {
      studentProfiles: [],
    },
  });

  const [loading, setLoading] = useState(false);
  const userId = useSelector((state: RootState) => state.user.self.id);
  const [paymentMethod, setPaymentMethod] = useState<'online' | 'offline'>('online');
  const [onlinePaymentType, setOnlinePaymentType] = useState<'send_invoice' | 'card_on_file'>('send_invoice');
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = methods;
  const facility = useCurrentFacility().data;
  const watchStudents = watch("studentProfiles");
  const [titleError, setTitleError] = useState("");
  const [messageError, setMessageError] = useState("");
  const [isActiveSubscriptionsModalOpen, setIsActiveSubscriptionsModalOpen] =
    useState(false);
  const { t } = useTranslation();

  const onlinePaymentTypeDropdownOptions = OnlinePaymentTypeOptions.filter(
    (opt) => opt.value !== 'checkout'
  );

  useEffect(() => {
    if (!stripeIntegrationEnabled) {
      setPaymentMethod('offline');
    } else if (product.collectType === 'offline') {
      setPaymentMethod('offline');
    } else if (product.collectType === 'both' && !product.onlineDefault) {
      setPaymentMethod('offline');
    } else {
      setPaymentMethod('online');
    }
  }, [product.collectType, product.onlineDefault, stripeIntegrationEnabled]);

  const loadStudents = async (
    inputValue: string,
    _currentOptions: [],
    additional: { page: number },
    newStudents: StudentProfileModel[] = [],
  ) => {
    const exceptIds = watchStudents.map((s: StudentProfileModel) => s.id) || [];
    const page = additional?.page || 1;

    // CN-2588: nest managed children under their family head (options[include][]=children)
    // so family members render indented under the parent account, matching the
    // session picker (CN-1323). The BE expands a matched child back to its head +
    // siblings, so families never split across search results.
    const query = inputValue
      ? { query: inputValue, per_page: 20, page, except: exceptIds, options: { include: ["children"] } }
      : { per_page: 20, page, except: exceptIds, options: { include: ["children"] } };

    try {
      const response = await schedulingFetchGet(
        `/facilities/${facility.id}/student_profiles`,
        query,
        { withHeaders: true },
      );

      const { students, headers } = response;
      const [current_page, total_pages] = [
        Number(headers.get("Current-Page")),
        Number(headers.get("Total-Pages")),
      ];
      const next_page = current_page < total_pages ? current_page + 1 : null;

      const allStudents = uniqBy(
        [...newStudents, ...flattenFamilyStudentProfiles(students)],
        "id",
      );

      return {
        options: allStudents || [],
        hasMore: current_page < total_pages,
        additional: {
          page: next_page,
        },
      };
    } catch (error) {
      console.error("Error fetching data:", error);
      return {
        options: [],
        hasMore: false,
        additional: {
          page: null,
        },
      };
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    const paymentData: any = {
      student_profile_ids: data.studentProfiles.map((student) => student.id),
      stripe_product_id: product.id,
      payment_method: paymentMethod,
    };

    if (paymentMethod === 'online') {
      paymentData.online_payment_type = onlinePaymentType;
    }

    if (!facilityId) {
      paymentData.coach_id = userId;
    }

    try {
      await schedulingFetchPost(
        `stripe_purchases/assign_subscription`,
        paymentData,
      );
      reset();
      modalHandler();
      toast.success(t("Athlete assigned successfully"));
    } catch (error) {
      console.error("Error creating product (full error):", error);
      const errors = error.errors;

      if (errors?.title || errors?.message) {
        setIsActiveSubscriptionsModalOpen(true);
        setTitleError(errors.title);
        setMessageError(errors.message);
      } else {
        console.error("Unexpected error structure:", error);
        toast.error(t("An unexpected error has occurred."));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ModalLayout
        isOpen={isModalOpen}
        onClose={modalHandler}
        classNames={`${styles.modal} event_form_container`}
      >
        <FormProvider {...methods}>
          <form>
            <div className={styles.modal_header}>
              <div className={styles.titleRow}>
                <h2
                  className={styles.truncatedText}
                  title={product.title.length > 25 ? product.name : ""}
                >
                  {t("Assign to")} "
                  {product.title.length > 25
                    ? `${product?.title?.substring(0, 25)}...`
                    : product.title}
                  "
                </h2>
                {product.title.length > 25 && (
                  <div className={styles.toolTipAfterEllipsis}>
                    <ToolTip text={product.title} orientation="right" />
                  </div>
                )}
              </div>
              <img
                src={closeIcon}
                alt="close"
                className={styles.close_icon}
                onClick={modalHandler}
              />
            </div>

            <div className={styles.add_view_form}>
              <p className={styles.helper_text}>
                {t('Choose a payment method, then select the athletes you want to assign to this subscription.')}
              </p>
              <div className={styles.fields_group}>
                <div className={styles.icon_row}>
                  <img src={DollarIcon} alt="" className={styles.row_icon} />
                  <div className={styles.row_content}>
                    <div className={styles.payment_row}>
                      <PaymentMethodDropdown
                        collectType={product.collectType || 'online'}
                        value={paymentMethod}
                        onChange={setPaymentMethod}
                        stripeIntegrationEnabled={stripeIntegrationEnabled}
                      />
                      {paymentMethod === 'online' && (
                        <div className={styles.online_payment_type_field}>
                          <label className={styles.sr_only}>{t('Online Payment Type')}</label>
                          <Select
                            options={onlinePaymentTypeDropdownOptions}
                            value={onlinePaymentTypeDropdownOptions.find(
                              (opt) => opt.value === onlinePaymentType
                            )}
                            onChange={(selected: any) =>
                              setOnlinePaymentType(selected?.value as 'send_invoice' | 'card_on_file')
                            }
                            styles={SelectStyles}
                            menuPortalTarget={document.body}
                            isSearchable={false}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles.icon_row}>
                  <img src={AccountCircleIcon} alt="" className={styles.row_icon} />
                  <div className={styles.row_content}>
                    <FormItem
                      error={errorMessageFor(errors, "studentProfiles")}
                      className={styles.input_field}
                    >
                      <label className={styles.sr_only}>{t("Select Athletes")}</label>
                      <SchedulingUserSelector
                        {...register("studentProfiles", {
                          validate: (ids) => ids.length > 0,
                        })}
                        control={control}
                        loadOptions={loadStudents}
                        placeholder={t("Select Athletes")}
                        multiSelect
                        canAddNewAthletes
                      />
                    </FormItem>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.button_wrapper}>
              <Button
                buttonType="light-primary"
                title={t("Cancel")}
                onClickFunc={modalHandler}
              />
              <Button
                buttonType="primary"
                title={loading ? t("Saving...") : t("Assign")}
                onClickFunc={handleSubmit(onSubmit)}
                disabled={loading || watchStudents.length === 0}
              />
            </div>
          </form>
        </FormProvider>
      </ModalLayout>
      {isActiveSubscriptionsModalOpen && (
        <ActiveSubscriptionsModal
          message={messageError}
          onClose={() => setIsActiveSubscriptionsModalOpen(false)}
          title={titleError}
        />
      )}
    </>
  );
};

export default memo(AssignOfferingModalSubscription);
